import parseSiren from 'siren-parser';
import { useEffect, useState } from 'react';
import _ from 'lodash';
import { useMeasurement, useBinLevelMeasurementOverrides, entityStore } from '~/utility/BinSentry-ui-utility';
import { useBarnBins, useBarnOrganizationBin } from '~/organizations/barns/barn-helpers';

export function useBarnLatestFeedLevel({ barnId }) {
  const { bins, loading: binsLoading } = useBarnBins({ barnId });
  const [data, setData] = useState();

  const { barnBin: barnBinEntity } = useBarnOrganizationBin({ barnId });
  const measurementOverrides = useBinLevelMeasurementOverrides({ bin: barnBinEntity });
  const binLevelMeasurement = useMeasurement({ name: 'binLevel', ...measurementOverrides });
  const { fullTotalWeight, loading: totalWeightLoading } = useBarnTotalWeight({ barnId });

  useEffect(() => {
    const fetchBinLevels = async () => {
      const binLevelHrefs = bins.map(bin => bin.getSubEntityByRel('https://api.binsentry.com/rel/bin-level-latest').href);
      const latestBinLevels = await Promise.all(binLevelHrefs.map(async href => parseSiren((await entityStore.fetchWait(href, false)).entity)));
      const weight = _.sumBy(latestBinLevels, binLevel => binLevel?.properties?.weight || 0);
      const volume = _.sumBy(latestBinLevels, binLevel => binLevel?.properties?.volume || 0);
      const estimatedVolume = _.sumBy(latestBinLevels, binLevel => binLevel?.properties?.estimatedVolume || 0);
      const estimatedWeight = _.sumBy(latestBinLevels, binLevel => binLevel?.properties?.estimatedWeight || 0);
      const totalVolume = _.sumBy(bins, bin => bin?.properties.volumes?.fullTotal || 0);
      const publishedAt = _.maxBy(latestBinLevels, binLevel => binLevel?.properties?.publishedAt)?.properties?.publishedAt;
      setData({ weight, volume, estimatedVolume, estimatedWeight, totalVolume, publishedAt });
    };

    if (!bins.length) {
      return;
    }
    setData();
    fetchBinLevels();
  }, [bins]);
  const loading = binsLoading || totalWeightLoading || !barnId || !data;

  if (loading) {
    return { loading };
  }

  const currentLevel = binLevelMeasurement.convert(data);
  const currentEstimatedLevel = binLevelMeasurement.convert({ ...data, volume: data.estimatedVolume, weight: data.estimatedWeight });
  const total = binLevelMeasurement.convert({ volume: data.totalVolume, weight: fullTotalWeight, totalVolume: data.totalVolume });

  return {
    ...data,
    currentLevel,
    currentEstimatedLevel,
    total,
    unit: binLevelMeasurement.unit,
    precision: binLevelMeasurement.precision,
    loading: false,
  };
}

export function useBarnTotalWeight({ barnId }) {
  const { bins, loading } = useBarnBins({ barnId });

  if (loading) {
    return { totalWeight: undefined, fullTotalWeight: undefined, loading };
  }

  return {
    totalWeight: _.sumBy(bins, bin => bin.properties.volumes.total * bin.properties.bulkDensity),
    fullTotalWeight: _.sumBy(bins, bin => bin.properties.volumes.fullTotal * bin.properties.bulkDensity),
    loading: false,
  };
}
